import { Card, type CardProps } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { AppLinkProps } from '@/hooks/usePathHelper'
import { Link } from '@tanstack/react-router'
import { forwardRef } from 'react'

export type LinkCardProps = Omit<CardProps, 'ref'> & {
  to?: AppLinkProps['to']
  params?: AppLinkProps['params']
  search?: AppLinkProps['search']
  /** A same-page section id; clicking scrolls to it even when it is already in the URL. */
  hash?: string
  disabled?: boolean
}

const LinkCard = forwardRef<HTMLDivElement, LinkCardProps>(
  ({ children, to, params, search, hash, className, disabled = false, ...props }, ref) => {
    const cardClasses = cn(
      'relative cursor-pointer overflow-hidden hover:has-[button:hover]:border-border hover:has-[button:hover]:shadow-none hover:has-[a:hover]:border-border hover:has-[a:hover]:shadow-none',
      disabled && 'cursor-not-allowed opacity-50',
      className
    )

    if (disabled) {
      return (
        <Card ref={ref} className={cardClasses} {...props}>
          {children}
        </Card>
      )
    }

    const linkProps: AppLinkProps = { to, params, search }

    return (
      <Link
        {...linkProps}
        hash={hash}
        onClick={(event) => {
          // A plain hash link does nothing once its hash is already in the URL, so scroll to
          // the same-page section ourselves. Otherwise the link navigates (and records the hash).
          const isPlainClick =
            event.button === 0 &&
            !event.metaKey &&
            !event.ctrlKey &&
            !event.shiftKey &&
            !event.altKey
          if (to !== '.' || !hash || !isPlainClick || window.location.hash !== `#${hash}`) return
          const target = document.getElementById(hash)
          if (!target) return
          event.preventDefault()
          target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }}
        className="flex flex-col cursor-default"
      >
        <Card ref={ref} interactive className={cardClasses} {...props}>
          {children}
        </Card>
      </Link>
    )
  }
)

export default LinkCard
